import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Button } from '@mui/material';
import { FaChartBar, FaSyncAlt, FaRegCalendarAlt } from 'react-icons/fa';
import {
  customerbaseddevices,
  customerbasedshift,
  telemetrykeydata,
} from '../../Services/app/operatorservice';

/**
 * TEEP — Total Effective Equipment Performance
 * -------------------------------------------------------------
 *   TEEP = OEE  ×  Utilization
 *   Utilization = actual machine RUN time ÷ total machine time
 *                 (run + idle + alarm + disconnect)
 *
 * OEE comes from the "oee" telemetry key (same source as the OEE screen).
 * Utilization is the real machine run-rate derived from the "total_duration"
 * telemetry (the same split that drives the KPI dashboard's "Run %"), NOT the
 * shift schedule — so a 24×7 plant no longer shows a flat 100%.
 */

const RANGE_OPTIONS = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
];

const Teep = () => {
  const dark = typeof document !== 'undefined' && document.body?.dataset?.theme === 'dark';
  const customerId = localStorage.getItem('CustomerID');

  const [devices, setDevices] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(localStorage.getItem('selectedGroup') || 'All');
  const [range, setRange] = useState('7');
  const [byMachine, setByMachine] = useState({}); // { deviceId: { oee, util } }
  const [loading, setLoading] = useState(false);

  const c = {
    text: dark ? '#e2e8f0' : '#1e293b',
    sub: dark ? '#94a3b8' : '#64748b',
    card: dark ? '#1e293b' : '#ffffff',
    border: dark ? '#334155' : '#e2e8f0',
    trackBg: dark ? '#334155' : '#e5e7eb',
  };

  // ── loaders (mirror the OEE screen) ────────────────────────
  const fetchMachineGroups = async () => {
    try {
      const data = await customerbasedshift(customerId, 'machinegroups');
      setMachineGroups(data?.[0]?.value || []);
    } catch (e) {
      console.error('TEEP: machine groups failed', e);
      setMachineGroups([]);
    }
  };

  const fetchDevices = async () => {
    try {
      const result = await customerbaseddevices(customerId, 100, 0);
      setDevices(result?.data?.data || result?.data || []);
    } catch (e) {
      console.error('TEEP: devices failed', e);
      setDevices([]);
    }
  };

  useEffect(() => {
    if (!customerId) return;
    fetchDevices();
    fetchMachineGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const filteredDevices = useMemo(() => {
    if (!selectedGroup || selectedGroup === 'All') return devices;
    const grp = machineGroups.find((g) => g.name === selectedGroup);
    if (!grp) return devices;
    return devices.filter((d) => grp.machines?.includes(d.name));
  }, [devices, machineGroups, selectedGroup]);

  const getWindow = () => {
    const to = Date.now();
    let from;
    if (range === '1') {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      from = d.getTime();
    } else {
      from = to - Number(range) * 24 * 3600 * 1000;
    }
    return { from, to };
  };

  // ── fetch OEE + run-based Utilization per machine ──────────
  const loadData = async () => {
    if (!filteredDevices || filteredDevices.length === 0) {
      setByMachine({});
      return;
    }
    setLoading(true);
    const { from, to } = getWindow();
    const results = {};
    await Promise.all(
      filteredDevices.map(async (device) => {
        const id = device?.id?.id;
        if (!id) return;
        try {
          const data = await telemetrykeydata(id, 'DEVICE', ['oee', 'total_duration'], from, to);

          // --- OEE : average of the oee series ---
          const oeeVals = (Array.isArray(data?.oee) ? data.oee : [])
            .map((p) => parseInt(p?.value, 10))
            .filter((n) => !Number.isNaN(n));
          const oee = oeeVals.length ? oeeVals.reduce((a, b) => a + b, 0) / oeeVals.length : null;

          // --- Utilization : run / (run+idle+alarm+disconnect) ---
          // total_duration accumulates & resets daily → take the latest point
          // of each calendar day, then sum the split across the window.
          const dayLatest = {};
          (Array.isArray(data?.total_duration) ? data.total_duration : []).forEach((pt) => {
            const ts = Number(pt?.ts);
            if (!ts) return;
            const d = new Date(ts);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            if (!dayLatest[key] || ts > dayLatest[key].ts) {
              try {
                dayLatest[key] = { ts, v: pt?.value ? JSON.parse(pt.value) : {} };
              } catch (err) {
                /* skip malformed point */
              }
            }
          });
          let run = 0;
          let total = 0;
          Object.values(dayLatest).forEach(({ v }) => {
            const r = Number(v?.total_run_duration || 0);
            const i = Number(v?.total_idle_duration || 0);
            const a = Number(v?.total_alarm_duration || 0);
            const dc = Number(v?.total_disconnect_duration || 0);
            run += r;
            total += r + i + a + dc;
          });
          const util = total > 0 ? (run / total) * 100 : null;

          results[id] = { oee, util };
        } catch (e) {
          console.error('TEEP: fetch failed for', device?.name, e);
          results[id] = { oee: null, util: null };
        }
      })
    );
    setByMachine(results);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDevices, range]);

  // ── rows ───────────────────────────────────────────────────
  const rows = useMemo(() => {
    return filteredDevices
      .map((d) => {
        const id = d?.id?.id;
        const m = byMachine[id] || {};
        const oee = m.oee;
        const util = m.util;
        const has = oee !== null && oee !== undefined && util !== null && util !== undefined;
        const teep = has ? (oee * util) / 100 : null;
        return { id, name: d?.name || '—', oee, util, teep, has };
      })
      .sort((a, b) => (b.teep ?? -1) - (a.teep ?? -1));
  }, [filteredDevices, byMachine]);

  const withData = rows.filter((r) => r.has);
  const avg = (arr, f) => (arr.length ? arr.reduce((s, r) => s + f(r), 0) / arr.length : 0);
  const avgOee = avg(withData, (r) => r.oee);
  const avgUtil = avg(withData, (r) => r.util);
  const avgTeep = avg(withData, (r) => r.teep);

  const teepColor = (v) => {
    if (v === null || v === undefined) return c.sub;
    if (v >= 40) return '#16a34a';
    if (v >= 20) return '#f97316';
    return '#dc2626';
  };
  const fmt = (v) => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);

  const SummaryCard = ({ label, value, color, hint }) => (
    <div style={{
      flex: 1, minWidth: 180, background: c.card, border: `1px solid ${c.border}`,
      borderRadius: 12, padding: '18px 20px', boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{ color: c.sub, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color, fontSize: 34, fontWeight: 700, marginTop: 6, lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ color: c.sub, fontSize: 12, marginTop: 6 }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{ padding: '70px 24px 32px', minHeight: '100vh', color: c.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <FaChartBar style={{ fontSize: 26, color: '#2563eb' }} />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>TEEP</h1>
      </div>
      <div style={{ color: c.sub, fontSize: 14, marginBottom: 20 }}>
        Total Effective Equipment Performance &nbsp;·&nbsp;
        <span style={{ fontWeight: 600 }}>TEEP = OEE × Utilization</span>
        &nbsp; (Utilization = actual machine run time ÷ total time)
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 22 }}>
        <FormControl size="small" style={{ minWidth: 190 }}>
          <InputLabel>Machine Group</InputLabel>
          <Select
            label="Machine Group" value={selectedGroup}
            onChange={(e) => { setSelectedGroup(e.target.value); localStorage.setItem('selectedGroup', e.target.value); }}
          >
            <MenuItem value="All">All Machines</MenuItem>
            {machineGroups.map((g) => (<MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>))}
          </Select>
        </FormControl>

        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel><FaRegCalendarAlt style={{ marginRight: 6, marginBottom: -2 }} />Range</InputLabel>
          <Select label="Range" value={range} onChange={(e) => setRange(e.target.value)}>
            {RANGE_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
          </Select>
        </FormControl>

        <Button
          variant="contained" onClick={loadData} disabled={loading}
          startIcon={<FaSyncAlt style={{ fontSize: 14 }} />}
          style={{ backgroundColor: '#f97316', textTransform: 'none' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 26 }}>
        <SummaryCard label="Average OEE" value={fmt(avgOee)} color="#2563eb" hint={`${withData.length} machine(s) with data`} />
        <SummaryCard label="Avg Utilization" value={fmt(avgUtil)} color="#f97316" hint="run ÷ (run+idle+alarm+disconnect)" />
        <SummaryCard label="Average TEEP" value={fmt(avgTeep)} color={teepColor(avgTeep)} hint="OEE × Utilization" />
      </div>

      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2.2fr', gap: 8, padding: '14px 20px',
          borderBottom: `1px solid ${c.border}`, fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, color: c.sub,
        }}>
          <div>Machine</div>
          <div style={{ textAlign: 'center' }}>OEE</div>
          <div style={{ textAlign: 'center' }}>Utilization</div>
          <div>TEEP</div>
        </div>

        {rows.length === 0 && (
          <div style={{ padding: '28px 20px', color: c.sub, fontSize: 14 }}>
            {loading ? 'Loading machine data…' : 'No machines to show for this selection.'}
          </div>
        )}

        {rows.map((r) => (
          <div key={r.id} style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2.2fr', gap: 8, padding: '13px 20px',
            borderBottom: `1px solid ${c.border}`, alignItems: 'center', fontSize: 14,
          }}>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            <div style={{ textAlign: 'center', color: '#2563eb', fontWeight: 600 }}>{fmt(r.oee)}</div>
            <div style={{ textAlign: 'center', color: '#f97316', fontWeight: 600 }}>{fmt(r.util)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 10, background: c.trackBg, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(r.teep ?? 0, 100)}%`, height: '100%',
                  background: teepColor(r.teep), borderRadius: 6, transition: 'width .3s',
                }} />
              </div>
              <div style={{ width: 46, textAlign: 'right', fontWeight: 700, color: teepColor(r.teep) }}>{fmt(r.teep)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Teep;
